import type { CityConfig } from "../city-template/types";

export const elCajonConfig: CityConfig = {
  city: "El Cajon",
  state: "California",
  stateAbbr: "CA",
  slug: "el-cajon",
  route: "/cities/el-cajon",
  title: "Emergency Passport El Cajon, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in El Cajon, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for East San Diego County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in El Cajon, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help El Cajon and East San Diego County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=El%20Cajon%20California%20suburban%20valley%20scene%20at%20golden%20hour%20with%20modern%20downtown%20buildings%20and%20the%20surrounding%20San%20Diego%20foothills%2C%20warm%20sunset%20light%20with%20warm%20amber%20and%20orange%20tones%2C%20green%20suburban%20landscape%20and%20distant%20mountains%2C%20clean%20and%20modern%20suburban%20scene%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20warm%20suburban%20environment&width=1800&height=700&seq=el-cajon-hero-001&orientation=landscape",
  heroImageAlt: "El Cajon California suburban valley scene at golden hour with downtown and San Diego foothills",
  geoPosition: "32.7948;-116.9625",
  geoPlacename: "El Cajon, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 2 hours from El Cajon via I-8 and I-5",
  },
  nearbyCitiesZips: [
    { name: "El Cajon", zips: "92019, 92020, 92021, 92022" },
    { name: "La Mesa", zips: "91941, 91942, 91943, 91944" },
    { name: "Santee", zips: "92071, 92072" },
    { name: "Spring Valley", zips: "91977, 91978" },
    { name: "Lakeside", zips: "92040" },
    { name: "Alpine", zips: "91901, 91903" },
    { name: "Jamul", zips: "91935" },
    { name: "Lemon Grove", zips: "91945, 91946" },
    { name: "San Diego", zips: "92101–92117" },
    { name: "Chula Vista", zips: "91910, 91911, 91913, 91914, 91915" },
    { name: "National City", zips: "91950, 91951" },
    { name: "Poway", zips: "92064, 92074" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent East San Diego County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Diego", route: "/cities/san-diego" },
    { name: "Chula Vista", route: "/cities/chula-vista" },
    { name: "Oceanside", route: "/cities/oceanside" },
    { name: "Carlsbad", route: "/cities/carlsbad" },
    { name: "Escondido", route: "/cities/escondido" },
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Long Beach", route: "/cities/long-beach" },
  ],
  fpcOfficeNote: "Fast Passport Center serves El Cajon remotely via secure courier. We do not have a walk-in office in El Cajon — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for East San Diego County travelers.",
  fpcOfficeDistance: "Serving all of East San Diego County",
  serviceAreaIntro: "From the downtown El Cajon area to the Fletcher Hills neighborhood, from the San Diego Foothills to the Spring Valley border — Fast Passport Center serves the entire El Cajon community. We regularly help travelers from La Mesa, Santee, Spring Valley, and Lakeside get their passports fast.",
  nearbyCitiesIntro: "We serve all El Cajon neighborhoods and nearby East San Diego County cities including La Mesa, Santee, Spring Valley, Lakeside, Alpine, and Lemon Grove. If you are in the inland San Diego area, we have you covered.",
  howItWorksIntro: "Most El Cajon travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With San Diego International Airport (SAN) and the border crossing at San Ysidro nearby, East County travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "El Cajon and East San Diego County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in El Cajon? Here are the most common ones we hear from East San Diego County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent East San Diego County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Rachel G.",
      location: "El Cajon, CA",
      rating: 5,
      text: "My family trip to Cancun was in 3 days and my daughter's passport was expired. Fast Passport Center got us a new one in 36 hours. Saved our vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Mark D.",
      location: "La Mesa, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Tokyo. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Sandra H.",
      location: "Santee, CA",
      rating: 5,
      text: "Lost my passport before my cruise to the Caribbean. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Chris B.",
      location: "Spring Valley, CA",
      rating: 5,
      text: "As a frequent traveler to Mexico from East County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "el-cajon-1",
      name: "El Cajon Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "401 W 2nd St",
      city: "El Cajon",
      state: "CA",
      zip: "92020",
      phone: "(619) 442-0693",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown El Cajon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "el-cajon-2",
      name: "La Mesa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7400 El Cajon Blvd",
      city: "La Mesa",
      state: "CA",
      zip: "91942",
      phone: "(619) 466-5550",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown La Mesa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "el-cajon-3",
      name: "Santee Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "9840 Mission Gorge Rd",
      city: "Santee",
      state: "CA",
      zip: "92071",
      phone: "(619) 449-1304",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Santee Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for East County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "el-cajon-4",
      name: "Lemon Grove Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8235 Broadway",
      city: "Lemon Grove",
      state: "CA",
      zip: "91945",
      phone: "(619) 460-8533",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Lemon Grove",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southeastern San Diego residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "el-cajon-5",
      name: "Spring Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1010 Elkelton Blvd",
      city: "Spring Valley",
      state: "CA",
      zip: "91977",
      phone: "(619) 670-8715",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from Sweetwater Reservoir",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern East County. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "el-cajon-6",
      name: "San Diego County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1600 Pacific Hwy",
      city: "San Diego",
      state: "CA",
      zip: "92101",
      phone: "(619) 237-0502",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "15 miles from El Cajon",
      website: "https://www.sdcounty.ca.gov/",
      websiteLabel: "San Diego County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "East County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Diego area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many East San Diego County applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many East County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "East County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in El Cajon." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your El Cajon address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in El Cajon before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in El Cajon for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most East San Diego County travelers." },
    { q: "How fast can I renew my passport in El Cajon?", a: "Fast Passport Center offers fast passport renewal in El Cajon in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in El Cajon in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near El Cajon?", a: "Same-day passport appointments are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for El Cajon residents." },
    { q: "Can I expedite a child passport in El Cajon?", a: "Yes. Fast Passport Center expedites child passports in El Cajon for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in El Cajon with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in El Cajon?",
      a: "With Fast Passport Center's emergency courier service, El Cajon travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in El Cajon?",
      a: "Popular options include the El Cajon Main Post Office (92020), La Mesa Post Office (91942), Santee Post Office (92071), and Lemon Grove Post Office (91945). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in El Cajon?",
      a: "Same-day passports are only available at the Los Angeles Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in the East County foothills or near Alpine?",
      a: "We serve all of East San Diego County including Alpine, Jamul, Lakeside, and Ramona. Our overnight shipping labels work from any UPS location, so your location within the county doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in El Cajon?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in El Cajon?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in El Cajon, La Mesa, and Santee offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in El Cajon?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in El Cajon?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk East San Diego County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — El Cajon", address: "555 N 2nd St, El Cajon, CA 92021", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — La Mesa", address: "5640 Lake Murray Blvd, La Mesa, CA 91942", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — El Cajon", address: "295 Jamacha Rd, El Cajon, CA 92019", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Santee", address: "9555 Mission Gorge Rd, Santee, CA 92071", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Lemon Grove", address: "3412 College Ave, Lemon Grove, CA 91945", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — El Cajon", address: "280 N 2nd St, El Cajon, CA 92021", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Spring Valley", address: "9835 Campo Rd, Spring Valley, CA 91977", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — La Mesa", address: "8950 Murray Dr, La Mesa, CA 91942", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — El Cajon",
      address: "1450 E Main St, El Cajon, CA 92021",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — La Mesa",
      address: "5500 Grossmont Center Dr, La Mesa, CA 91942",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Santee",
      address: "8920 Arrow Route, Santee, CA 92071",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — San Diego",
      address: "4375 Derrick Dr, San Diego, CA 92117",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service mega center for San Diego County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "20 miles from El Cajon",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "The primary international airport for San Diego County. International flights to Mexico, Canada, Europe, and Asia.",
      flightUrl: "https://www.san.org/",
      flightLabel: "San Diego Airport",
      featured: true,
    },
    {
      code: "TIJ",
      name: "Tijuana International Airport",
      distance: "25 miles from El Cajon",
      address: "Carretera Aeropuerto, Tijuana, Baja California, Mexico",
      note: "Cross-border Xpress bridge available for international flights from Tijuana. Popular for budget travel to Mexico and Latin America.",
      flightUrl: "https://www.tijuana-airport.com/",
      flightLabel: "Tijuana Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "120 miles from El Cajon",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-8 and I-5.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
};